import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AnimatePresence, motion } from 'framer-motion';
import { Wine, Users, Sparkles, Truck, X, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const SOUR_HERO = '/images/soursweet-festival.png';

const COCKTAIL_IMAGES = [
  '/images/takeaway-cocktail-tray.jpg',
  '/images/fruchtige-takeaway-cocktails.jpg',
  '/images/takeaway-cocktail-set.jpg',
  '/images/takeaway-orange-cocktails.jpg',
  '/images/takeaway-dark-cocktails.jpg',
  '/images/cocktail-kit-takeaway.jpg',
  '/images/takeaway-red-cocktail.jpg',
  '/images/takeaway-mix-cocktails.png',
];

const featureIcons = [Users, Wine, Sparkles, Truck];

export const Sour = () => {
  const { t, language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Preload all images on mount
  useEffect(() => {
    COCKTAIL_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    document.title = language === 'de' ? 'SourSweet.de/Sour - Cocktails' : 'SourSweet.de/Sour - Cocktails';
  }, [language]);

  const features = t('sour.features') || [];

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(COCKTAIL_IMAGES[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = useCallback(() => {
    const newIndex = currentIndex === 0 ? COCKTAIL_IMAGES.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(COCKTAIL_IMAGES[newIndex]);
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    const newIndex = currentIndex === COCKTAIL_IMAGES.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(COCKTAIL_IMAGES[newIndex]);
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, goToPrevious, goToNext]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  return (
    <div className="page-transition" data-testid="sour-page">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-[400px] md:min-h-[500px] lg:min-h-[450px] py-16 sm:py-20 md:py-24 pt-24 sm:pt-28 md:pt-32 lg:pt-36 pb-16 sm:pb-24 md:pb-44 overflow-hidden" data-testid="sour-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center md:bg-bottom lg:bg-center lg:bg-fixed"
          style={{ backgroundImage: `url(${SOUR_HERO})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 via-emerald-900/65 to-emerald-900/45" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <div>
            <div className="flex items-center justify-center gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-lime-400/20 flex items-center justify-center">
                <Wine className="text-lime-300" size={18} />
              </div>
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-3 sm:mb-4" data-testid="sour-title">
              {t('sour.title')}
            </h1>

            <p className="text-sm sm:text-base text-lime-100 font-semibold max-w-xl mx-auto px-4">
              {t('sour.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-emerald-800" data-testid="sour-gallery">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-2xl md:text-3xl font-bold text-white text-center mb-8"
          >
            {language === 'de' ? 'Unsere Kreationen' : 'Our Creations'}
          </motion.h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {COCKTAIL_IMAGES.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                onClick={() => openLightbox(index)}
                className="aspect-square rounded-xl overflow-hidden border border-emerald-600 hover:border-yellow-300 shadow-md hover:shadow-[0_0_50px_rgba(253,224,71,0.8)] transition-all duration-300 cursor-pointer"
              >
                <img
                  src={image}
                  alt={`Cocktail ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
            onClick={closeLightbox}
            data-testid="sour-lightbox-modal"
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="sour-lightbox-close"
            >
              <X className="text-white" size={28} />
            </button>

            {/* Previous Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-4 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 hover:shadow-[0_0_20px_rgba(251,146,60,0.6)] transition-all"
              data-testid="sour-lightbox-prev"
            >
              <ChevronLeft className="text-white" size={32} />
            </button>

            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-4 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 hover:shadow-[0_0_20px_rgba(251,146,60,0.6)] transition-all"
              data-testid="sour-lightbox-next"
            >
              <ChevronRight className="text-white" size={32} />
            </button>

            {/* Image Container */}
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0.8 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15 }}
              className="relative max-w-5xl max-h-[85vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt={`Cocktail ${currentIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
              
              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 px-4 py-2 rounded-full">
                <span className="text-white text-sm font-semibold">
                  {currentIndex + 1} / {COCKTAIL_IMAGES.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-12 bg-emerald-950" data-testid="sour-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              {t('contact.subtitle')}
            </h2>
            <Link
              to={language === 'de' ? '/reservierung' : '/reservation'}
              className="inline-flex items-center gap-2 bg-yellow-400 text-emerald-950 border border-yellow-300 hover:bg-yellow-300 hover:shadow-[0_0_50px_rgba(253,224,71,0.8)] transition-all duration-300 rounded-full px-8 py-3 font-bold"
            >
              {t('hero.cta')}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
